import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './features/auth/AuthContext';
import ProtectedRoute from './components/guards/ProtectedRoute';
import RoleBasedRoute from './components/guards/RoleBasedRoute';
import SessionManager from './components/SessionManager';
import { ROLES } from './utils/rbac';
import ErrorBoundary from './components/ErrorBoundary';


// Pages
import Splash from './pages/Splash';
import Login from './features/auth/LoginPage';
import Register from './features/auth/RegisterPage';

import BigFiveTest from './features/assessments/bigfive/BigFivePage';
import ProductivityTest from './features/assessments/productivity/ProductivityPage';
import Results from './features/assessments/Results';
import AdminDashboard from './pages/AdminDashboard';
import AdminDashboardPageEnhanced from './features/dashboard/AdminDashboard';
import APIManagementDashboard from './pages/APIManagementDashboard';
import HrDashboardPageEnhanced from './features/dashboard/HRDashboard';
import CustomerAdminDashboardEnhanced from './features/dashboard/CustomerDashboard';
import PerformancePageEnhanced from './pages/PerformancePageEnhanced';
import ReportsPageEnhanced from './features/reports/ReportsList';
import UserManagementPageEnhanced from './features/users/UserManagement';
import SettingsPageEnhanced from './features/settings/SettingsPage';
import AssessmentStartPage from './features/assessments/AssessmentStart';
import ThankYouPage from './pages/ThankYouPage';
import EduTechLanding from './features/assessments/edutech/EduTechLanding';
import EduTechAssessment from './pages/EduTechAssessmentPage';
import EduTechResult from './features/assessments/edutech/EduTechResult';
import { ASSESSMENT_REGISTRY } from './features/assessments/registry';
import { APTITUDE_ROUTES, COGNITIVE_ROUTES, SKILL_ROUTES, MEGA_ROUTES } from './routes/assessmentRoutes';
import AptitudeIntroPage from './pages/assessment/aptitude/IntroPage';
import AptitudeTestPage from './pages/assessment/aptitude/TestPage';
import AptitudeResultPage from './pages/assessment/aptitude/ResultPage';
import CognitiveIntroPage from './pages/assessment/cognitive/IntroPage';
import CognitiveTestPage from './pages/assessment/cognitive/TestPage';
import CognitiveResultPage from './pages/assessment/cognitive/ResultPage';
import SkillIntroPage from './pages/assessment/skill/IntroPage';
import SkillTestPage from './pages/assessment/skill/TestPage';
import SkillResultPage from './pages/assessment/skill/ResultPage';
import MegaIntroPage from './pages/assessment/mega/IntroPage';
import MegaTestPage from './pages/assessment/mega/TestPage';
import MegaResultPage from './pages/assessment/mega/ResultPage';
import SharedVerifyPage from './components/assessment/SharedVerifyPage';
import WorkscoreLandingPage from './pages/workscore/LandingPage';
import WorkscoreIntroPage from './pages/workscore/IntroPage';
import WorkscoreInstructionsPage from './pages/workscore/InstructionsPage';
import WorkscoreSectionPage from './pages/workscore/SectionPage';
import WorkscoreResultsPage from './pages/workscore/ResultsPage';

// Extended Assessment Module
import ExtendedAssessment from './features/assessments/extended/ExtendedPage';

// Components
import Layout from './components/Layout';
import DashboardLayout from './components/DashboardLayout';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// ADDED: shared verify route wrapper so all assessments use one unified verification page.
function AssessmentVerifyRoute({ assessmentKey, assessmentLabel, nextPath }) {
  const navigate = useNavigate();
  return (
    <SharedVerifyPage
      assessmentKey={assessmentKey}
      assessmentLabel={assessmentLabel}
      onVerified={() => navigate(nextPath)}
    />
  );
}

function App() {
  const edutechEnabled = Boolean(ASSESSMENT_REGISTRY.find((a) => a.key === 'edutech')?.enabled);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <Router>
            <SessionManager>
              <div className="min-h-screen bg-gray-50">
              <Toaster 
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: '#363636',
                    color: '#fff',
                  },
                  success: {
                    duration: 3000,
                    iconTheme: {
                      primary: '#4ade80',
                      secondary: '#fff',
                    },
                  },
                  error: {
                    duration: 4000,
                    iconTheme: {
                      primary: '#ef4444',
                      secondary: '#fff',
                    },
                  },
                }}
              />
            
            <Routes>
              {/* Splash screen */}
              <Route path="/" element={<Splash />} />
              
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              

              
              {/* Assessment Flow for Users */}
              <Route path="/assessment" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER]}>
                    <AssessmentStartPage />
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/thank-you" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER]}>
                    <ThankYouPage />
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              
              {/* Extended Assessment - User Only - No Layout to hide sidebar */}
              <Route path="/assessment/extended" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER]}>
                    <ExtendedAssessment />
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />

              <Route path="/assessment/bigfive" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER]}>
                    <BigFiveTest />
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />

              <Route path="/assessment/productivity" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER]}>
                    <ProductivityTest />
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />

              <Route path="/assessment/edutech" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER]}>
                    {edutechEnabled ? <EduTechAssessment /> : <Navigate to="/assessment" replace />}
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />

              <Route path="/assessment/edutech/start" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER]}>
                    {edutechEnabled ? <EduTechLanding /> : <Navigate to="/assessment" replace />}
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />

              <Route path="/assessment/edutech/result/:id" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER]}>
                    {edutechEnabled ? <EduTechResult /> : <Navigate to="/assessment" replace />}
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />

              <Route path="/workscore" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER]}>
                    <WorkscoreLandingPage />
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />

              <Route path="/workscore/:slug" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER]}>
                    <WorkscoreIntroPage />
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />

              <Route path="/workscore/:slug/instructions" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER]}>
                    <WorkscoreInstructionsPage />
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />

              <Route path="/workscore/:slug/section/:sectionNumber" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER]}>
                    <WorkscoreSectionPage />
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />

              <Route path="/workscore/:slug/results" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER]}>
                    <WorkscoreResultsPage />
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />

              <Route path="/assessment/salesforce" element={<Navigate to="/assessment/salesforce/verify" replace />} />
              <Route path="/assessment/customercare" element={<Navigate to="/assessment/customercare/verify" replace />} />
              <Route path="/assessment/callingsales" element={<Navigate to="/assessment/callingsales/verify" replace />} />
              <Route path="/assessment/feetonstreet" element={<Navigate to="/assessment/feetonstreet/verify" replace />} />

              <Route path="/assessment/salesforce/verify" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER]}>
                    <AssessmentVerifyRoute
                      assessmentKey="salesforce"
                      assessmentLabel="Salesforce AI WorkScore"
                      nextPath="/workscore/salesforce"
                    />
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />

              <Route path="/assessment/customercare/verify" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER]}>
                    <AssessmentVerifyRoute
                      assessmentKey="customercare"
                      assessmentLabel="CustomerCare AI WorkScore"
                      nextPath="/workscore/customercare"
                    />
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />

              <Route path="/assessment/callingsales/verify" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER]}>
                    <AssessmentVerifyRoute
                      assessmentKey="callingsales"
                      assessmentLabel="CallingSales AI WorkScore"
                      nextPath="/workscore/callingsales"
                    />
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />

              <Route path="/assessment/feetonstreet/verify" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER]}>
                    <AssessmentVerifyRoute
                      assessmentKey="feetonstreet"
                      assessmentLabel="FeetOnStreet AI WorkScore"
                      nextPath="/workscore/feetonstreet"
                    />
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />

              <Route path={APTITUDE_ROUTES.base} element={<Navigate to={APTITUDE_ROUTES.verify} replace />} />

              <Route path={APTITUDE_ROUTES.verify} element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER]}>
                    <AssessmentVerifyRoute
                      assessmentKey="aptitude"
                      assessmentLabel="Aptitude Assessment"
                      nextPath={APTITUDE_ROUTES.intro}
                    />
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />

              <Route path={APTITUDE_ROUTES.intro} element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER]}>
                    <AptitudeIntroPage />
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />

              <Route path={APTITUDE_ROUTES.test} element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER]}>
                    <AptitudeTestPage />
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />

              <Route path={APTITUDE_ROUTES.result} element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER]}>
                    <AptitudeResultPage />
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />

              <Route path={COGNITIVE_ROUTES.base} element={<Navigate to={COGNITIVE_ROUTES.verify} replace />} />

              <Route path={COGNITIVE_ROUTES.verify} element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER]}>
                    <AssessmentVerifyRoute
                      assessmentKey="cognitive"
                      assessmentLabel="Cognitive Assessment"
                      nextPath={COGNITIVE_ROUTES.intro}
                    />
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />

              <Route path={COGNITIVE_ROUTES.intro} element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER]}>
                    <CognitiveIntroPage />
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />

              <Route path={COGNITIVE_ROUTES.test} element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER]}>
                    <CognitiveTestPage />
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />

              <Route path={COGNITIVE_ROUTES.result} element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER]}>
                    <CognitiveResultPage />
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />

              <Route path={SKILL_ROUTES.base} element={<Navigate to={SKILL_ROUTES.verify} replace />} />

              <Route path={SKILL_ROUTES.verify} element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER]}>
                    <AssessmentVerifyRoute
                      assessmentKey="skill"
                      assessmentLabel="Skill Assessment"
                      nextPath={SKILL_ROUTES.intro}
                    />
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />

              <Route path={SKILL_ROUTES.intro} element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER]}>
                    <SkillIntroPage />
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />

              <Route path={SKILL_ROUTES.test} element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER]}>
                    <SkillTestPage />
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />

              <Route path={SKILL_ROUTES.result} element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER]}>
                    <SkillResultPage />
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />

              <Route path={MEGA_ROUTES.base} element={<Navigate to={MEGA_ROUTES.verify} replace />} />

              <Route path={MEGA_ROUTES.verify} element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER]}>
                    <AssessmentVerifyRoute
                      assessmentKey="mega"
                      assessmentLabel="Mega Assessment"
                      nextPath={MEGA_ROUTES.intro}
                    />
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />

              <Route path="/assessment/extended/verify" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER]}>
                    <AssessmentVerifyRoute
                      assessmentKey="extended"
                      assessmentLabel="Extended Assessment"
                      nextPath="/assessment/extended"
                    />
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />

              <Route path="/assessment/bigfive_productivity/verify" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER]}>
                    <AssessmentVerifyRoute
                      assessmentKey="bigfive_productivity"
                      assessmentLabel="Big Five + Productivity Assessment"
                      nextPath="/assessment/bigfive"
                    />
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />

              <Route path="/assessment/edutech/verify" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER]}>
                    {edutechEnabled ? (
                      <AssessmentVerifyRoute
                        assessmentKey="edutech"
                        assessmentLabel="EduTech Assessment"
                        nextPath="/assessment/edutech"
                      />
                    ) : (
                      <Navigate to="/assessment" replace />
                    )}
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />

              <Route path={MEGA_ROUTES.intro} element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER]}>
                    <MegaIntroPage />
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />

              <Route path={MEGA_ROUTES.test} element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER]}>
                    <MegaTestPage />
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />

              <Route path={MEGA_ROUTES.result} element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER]}>
                    <MegaResultPage />
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/test/bigfive" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER, ROLES.HR, ROLES.ADMIN]}>
                    <BigFiveTest />
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/test/productivity" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.USER, ROLES.HR, ROLES.ADMIN]}>
                    <ProductivityTest />
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/results" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.HR]}>
                    <Layout>
                      <Results />
                    </Layout>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/results/:attemptId" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.HR]}>
                    <Layout>
                      <Results />
                    </Layout>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              
              {/* Admin routes */}
              <Route path="/admin" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN]}>
                    <Layout>
                      <AdminDashboard />
                    </Layout>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              
              {/* Customer Management - Admin Only */}
              <Route path="/customer-management" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN]}>
                    <Layout>
                      <AdminDashboard />
                    </Layout>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              
              {/* Dashboard routes */}
              <Route path="/admin/dashboard" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN]}>
                    <DashboardLayout>
                      <AdminDashboardPageEnhanced />
                    </DashboardLayout>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/admin/performance" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN]}>
                    <DashboardLayout>
                      <PerformancePageEnhanced />
                    </DashboardLayout>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/admin/api-management" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN]}>
                    <DashboardLayout>
                      <APIManagementDashboard />
                    </DashboardLayout>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/admin-dashboard" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN]}>
                    <DashboardLayout>
                      <AdminDashboardPageEnhanced />
                    </DashboardLayout>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              
              {/* Customer Admin Dashboard */}
              <Route path="/customer/dashboard" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.CUSTOMER_ADMIN, ROLES.ADMIN]}>
                    <DashboardLayout>
                      <CustomerAdminDashboardEnhanced />
                    </DashboardLayout>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/customer-dashboard" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.CUSTOMER_ADMIN, ROLES.ADMIN]}>
                    <DashboardLayout>
                      <CustomerAdminDashboardEnhanced />
                    </DashboardLayout>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/hr/dashboard" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.HR]}>
                    <DashboardLayout>
                      <HrDashboardPageEnhanced />
                    </DashboardLayout>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/hr-dashboard" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.HR]}>
                    <DashboardLayout>
                      <HrDashboardPageEnhanced />
                    </DashboardLayout>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/performance" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN]}>
                    <Layout>
                      <PerformancePageEnhanced />
                    </Layout>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              
              {/* Reports route */}
              <Route path="/reports" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.HR]}>
                    <DashboardLayout>
                      <ReportsPageEnhanced />
                    </DashboardLayout>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              
              {/* User Management route */}
              <Route path="/user-management" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.HR]}>
                    <DashboardLayout>
                      <UserManagementPageEnhanced />
                    </DashboardLayout>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              
              {/* Settings route */}
              <Route path="/settings" element={
                <ProtectedRoute>
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.HR]}>
                    <DashboardLayout>
                      <SettingsPageEnhanced />
                    </DashboardLayout>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              
              {/* Catch all route - redirect to splash */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
              </div>
            </SessionManager>
          </Router>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
